/* Room walkthrough. Scroll drives a camera through one room:
   1. Doorway: a short film steps through the door (desktop), the heading lines slide apart.
   2. Each stop: its travel film is scrubbed by scroll (the camera physically moves to the object), the film lands on the
      sharp close-up, which keeps pushing in slowly while the card is shown and the stop's name runs across the scene.
      A stop without a film falls back to a camera move over the room photograph.
   3. End: the doorway film runs backwards, the camera backs out of the room, and every object is pinned on the photo.
   The camera eases toward the scroll position (scrolling itself is never taken over). Runs only with full motion
   (html.nx-motion); otherwise the section stays a readable page. */
const html = document.documentElement;
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const mix = (a, b, t) => a + (b - a) * t;
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const ramp = (a, b, t) => clamp((t - a) / (b - a));
const phone = matchMedia('(max-width: 749px)');
const desk = matchMedia('(min-width: 990px)');

/* Frame sheets: one 4x6 sprite (24 frames) is cut into separate frame bitmaps once, so each scroll tick draws one small,
   sharp frame instead of sampling the whole 5120px sheet (slow) and blending two frames (a ghosted, blurry image). */
const COLS = 4, ROWS = 6, FRAMES = 24;
/* Low-memory or few-core devices get the smaller sheets (a quarter of the pixels) so scrolling stays smooth. */
const WEAK = (navigator.deviceMemory || 8) <= 4 || (navigator.hardwareConcurrency || 8) <= 4;
/* Scroll values are written on the few elements that use them, and only when they change: writing them on the section
   made the browser restyle the whole walkthrough on every scroll tick (measured ~25 ms, i.e. dropped frames). */
const put = (els, name, v) => { for (const el of els) if (el && el['__' + name] !== v) { el['__' + name] = v; el.style.setProperty(name, v); } };
function drawSheet(c, t) {
  const frames = c.__frames;
  if (!frames) return;
  const i = Math.round(clamp(t) * (FRAMES - 1));
  if (c.__last === i) return;
  c.__last = i;
  c.__ctx.drawImage(frames[i], 0, 0);
}

/* Seek a video toward a target time without queueing seeks; the last request wins */
function seekTo(v, t) {
  if (v && v.tagName === 'CANVAS') { drawSheet(v, t); return; }
  if (!v || v.readyState < 1 || !v.duration) return;
  const at = clamp(t) * (v.duration - 0.04);
  v.__want = at;
  if (v.__busy) return;
  if (Math.abs(v.currentTime - at) < 1 / 48) return;
  v.__busy = true;
  v.currentTime = at;
  v.addEventListener('seeked', () => { v.__busy = false; if (Math.abs(v.currentTime - v.__want) >= 1 / 48) seekTo(v, v.__want / (v.duration - 0.04)); }, { once: true });
}

/* Stops far from the camera give their frames back (each stop holds ~90 MB of decoded frames); they reload when near. */
function release(v) {
  if (!v || v.tagName !== 'CANVAS' || !v.__armed) return;
  v.__armed = false; v.__ok = false; v.__gen = (v.__gen || 0) + 1;
  if (v.__frames) { v.__frames.forEach((f) => f.close()); v.__frames = null; }
}

function arm(v) {
  if (v && v.tagName === 'CANVAS') {
    if (v.__armed) return;
    v.__armed = true;
    const gen = v.__gen = (v.__gen || 0) + 1; // a later release/arm makes this load stale
    const img = new Image();
    img.decoding = 'async';
    img.onload = async () => {
      const fw = img.naturalWidth / COLS, fh = img.naturalHeight / ROWS;
      const frames = [];
      /* cut the sheet in small slices with a pause between them, so the page keeps scrolling while a stop loads */
      try {
        for (let n = 0; n < FRAMES; n += 3) {
          frames.push(...await Promise.all(Array.from({ length: Math.min(3, FRAMES - n) }, (_, j) => createImageBitmap(img, ((n + j) % COLS) * fw, Math.floor((n + j) / COLS) * fh, fw, fh))));
          if (v.__gen !== gen) { frames.forEach((f) => f.close()); return; }
          await new Promise((r) => setTimeout(r, 16));
        }
      } catch (e) { frames.forEach((f) => f.close()); return; } // no bitmaps: the stop keeps its camera-move fallback
      img.src = ''; // the decoded 5120px sheet is ~90 MB; the frames are all we keep
      v.__frames = frames;
      v.width = fw; v.height = fh; v.__ctx = v.getContext('2d', { alpha: false });
      v.__ok = true; v.__last = -1; drawSheet(v, 0); v.dispatchEvent(new Event('loadeddata'));
    };
    img.src = (phone.matches || WEAK) && v.dataset.sheetM ? v.dataset.sheetM : v.dataset.sheet;
    return;
  }
  if (!v || v.__armed || !v.dataset.src) return;
  v.__armed = true;
  /* a failed load (network hiccup, file still processing) gets one retry; until then the stop uses the camera move */
  v.addEventListener('error', () => { if (v.__retried) return; v.__retried = true; setTimeout(() => { v.load(); v.pause(); }, 2000); }, { once: true });
  v.addEventListener('loadeddata', () => { v.__ok = true; }, { once: true });
  v.src = phone.matches && v.dataset.srcM ? v.dataset.srcM : v.dataset.src;
  v.preload = 'auto';
  v.load();
  v.pause();
}

class Walk {
  constructor(sec) {
    this.sec = sec;
    this.track = sec.querySelector('[data-walk-track]') || sec;
    this.stage = sec.querySelector('[data-walk-stage]');
    this.cam = sec.querySelector('[data-walk-cam]');
    this.room = sec.querySelector('[data-walk-room]');
    this.film = sec.querySelector('[data-walk-film]');
    this.stops = [...sec.querySelectorAll('[data-walk-stop]')];
    this.moves = this.stops.map((el) => el.querySelector('[data-walk-move]'));
    this.ticks = [...sec.querySelectorAll('[data-walk-tick]')];
    this.rail = sec.querySelector('[data-walk-rail]');
    this.intro = [sec.querySelector('.nx-walk__intro')];
    this.endEls = [...sec.querySelectorAll('.nx-walk__spot, .nx-walk__end')];
    this.parts = this.stops.map((el) => ({ shot: [el.querySelector('.nx-walk__shot')], move: [el.querySelector('.nx-walk__move')], big: [el.querySelector('.nx-walk__big')], card: [el.querySelector('.nx-walk__card')] }));
    /* progress bar as a real element (the old ::after could only be fed from the stage, which restyled everything in it) */
    this.bar = document.createElement('i');
    this.bar.setAttribute('aria-hidden', 'true');
    Object.assign(this.bar.style, { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5, height: '3px', background: 'var(--glow)', transformOrigin: 'left', transform: 'scaleX(0)', pointerEvents: 'none' });
    this.stage.appendChild(this.bar);
    this.n = this.stops.length;
    this.units = this.n + 2;
    this.p = 0; this.pending = false; this.active = -2;
    sec.setAttribute('data-walk-on', '');
    if (this.rail) this.rail.hidden = false;
    this.ticks.forEach((t, k) => t.addEventListener('click', () => this.goTo(k)));
    const kick = () => this.kick();
    [window, document.querySelector('.page-wrapper')].filter(Boolean).forEach((s) => s.addEventListener('scroll', kick, { passive: true }));
    addEventListener('resize', () => { this.layout(); kick(); }, { passive: true });
    phone.addEventListener('change', () => { this.layout(); kick(); });
    if (this.room && !this.room.complete) this.room.addEventListener('load', () => { this.layout(); kick(); }, { once: true });
    [this.film, ...this.moves].forEach((v) => v && v.addEventListener('loadeddata', kick));
    if (this.film && phone.matches) { this.film.remove(); this.film = null; }
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) { io.disconnect(); arm(this.moves[0] ? this.moves[0] : this.film); } }, { rootMargin: '100% 0px' });
      io.observe(sec);
    } else { arm(this.moves[0] ? this.moves[0] : this.film); }
    this.layout();
    this.p = this.target();
    this.render(this.p);
  }

  layout() {
    const W = this.stage.clientWidth, H = this.stage.clientHeight;
    const nw = this.room && this.room.naturalWidth, nh = this.room && this.room.naturalHeight;
    const aspect = nw && nh ? nw / nh : (phone.matches ? 3072 / 5504 : 5504 / 3072);
    const cw = Math.max(W, H * aspect), ch = cw / aspect;
    Object.assign(this.cam.style, { width: `${cw}px`, height: `${ch}px` });
    const at = (fx, fy, s, ax, ay) => ({
      tx: clamp(ax * W - s * fx * cw, W - s * cw, 0),
      ty: clamp(ay * H - s * fy * ch, H - s * ch, 0),
      s,
    });
    this.home = at(0.5, 0.5, 1, 0.5, 0.5);
    this.door = at(0.5, 0.56, 1.12, 0.5, 0.5);
    this.at0 = at(0.5, 0.5, 1.04, 0.5, 0.5);
    this.shots = this.stops.map((el) => {
      const d = el.dataset, m = phone.matches;
      const fx = (m ? +d.mx : +d.x) / 100, fy = (m ? +d.my : +d.y) / 100;
      return at(fx, fy, (+d.z || 2) * (m ? 0.8 : 1), m ? 0.5 : 0.62, m ? 0.4 : 0.48);
    });
    this.top = parseFloat(getComputedStyle(this.stage).top) || 0; // read once per layout, not on every scroll tick
  }

  target() {
    const travel = this.track.offsetHeight - this.stage.offsetHeight;
    return travel > 0 ? clamp(-(this.track.getBoundingClientRect().top - this.top) / travel) : 0;
  }

  kick() {
    if (this.pending) return;
    this.pending = true;
    const run = () => { if (!this.pending) return; this.pending = false; this.step(); };
    requestAnimationFrame(run);
    setTimeout(run, 50);
  }

  step() {
    const want = this.target();
    this.p += (want - this.p) * 0.24;
    if (Math.abs(want - this.p) < 0.0003) this.p = want;
    this.render(this.p);
    if (this.p !== want) this.kick();
  }

  render(p) {
    const u = p * this.units;
    const lerp = (a, b, t) => ({ tx: mix(a.tx, b.tx, t), ty: mix(a.ty, b.ty, t), s: mix(a.s, b.s, t) });
    const film = this.film && this.film.__ok ? this.film : null;
    let cam = this.home, active = -1, filmShow = 0, end = 0;
    put(this.intro, '--q0', clamp(u).toFixed(3));
    this.bar.style.transform = `scaleX(${p.toFixed(4)})`;

    /* per-stop state: m = travel film opacity, a = close-up opacity, h = hold progress, k = whole-stop progress */
    const state = this.stops.map(() => ({ m: 0, a: 0, h: 0, k: 0, c: 0, card: false, t: -1 }));

    if (u < 1) {
      const t = clamp(u);
      /* With a travel film into the first stop, the doorway is a still that breathes in slightly and hands over to that
         film; without one, the doorway film (or a camera push) carries the step through the door. */
      if (this.moves[0]) cam = lerp(this.home, this.at0 || this.home, ease(t));
      else {
        cam = lerp(this.home, this.door, ease(t));
        if (film) { filmShow = 1; seekTo(film, t / 0.95); }
      }
    } else if (u < this.n + 1) {
      const k = Math.floor(u - 1), t = u - 1 - k, st = state[k];
      const move = this.moves[k], ready = !!(move && move.__ok);
      active = k; st.t = t; st.k = t;
      const travel = ramp(0, 0.45, t);
      if (ready) {
        st.m = 1;
        seekTo(move, travel);
        cam = k === 0 ? this.at0 : this.shots[k - 1];
        st.a = ramp(0.42, 0.5, t);
      } else {
        cam = lerp(k === 0 ? this.door : this.shots[k - 1], this.shots[k], ease(travel));
        st.a = ramp(0.36, 0.56, t);
        /* next film not ready yet: hold the previous close-up and fade straight to this one (never flash the room) */
        if (k > 0) state[k - 1].a = 1 - st.a;
      }
      if (film && k === 0 && !ready) filmShow = 1 - ramp(0, 0.2, t);
      st.c = ramp(0.5, 0.66, t) * (1 - ramp(0.9, 0.98, t));
      st.h = ramp(0.45, 0.75, t) * (1 - ramp(0.82, 0.98, t)); /* push in, then settle back so the next film starts on the same frame */
      st.card = t > 0.5 && t < 0.98;
    } else {
      /* back out of the room: last close-up -> doorway film backwards -> the whole room */
      const t = clamp(u - this.n - 1), last = state[this.n - 1];
      if (last) { last.a = 1 - ramp(0, 0.12, t); last.h = 1; }
      if (film) {
        filmShow = ramp(0, 0.1, t) * (1 - ramp(0.72, 0.84, t));
        seekTo(film, 1 - ramp(0.08, 0.74, t));
        cam = this.home;
      } else {
        cam = lerp(this.shots[this.n - 1] || this.door, this.home, ease(ramp(0, 0.6, t)));
      }
      end = ramp(0.7, 0.92, t);
    }

    this.cam.style.transform = `translate3d(${cam.tx.toFixed(1)}px, ${cam.ty.toFixed(1)}px, 0) scale(${cam.s.toFixed(4)})`;
    put([this.film], '--film', filmShow.toFixed(3));
    put(this.endEls, '--end', end.toFixed(3));
    if (this.sec.__end !== end > 0.5) { this.sec.__end = end > 0.5; this.sec.classList.toggle('is-end', end > 0.5); }

    if (active !== this.active) {
      this.active = active;
      this.stops.forEach((el, i) => {
        const near = i === active || i === active + 1 || i === active - 1 || (active === -1 && i === 0 && u < 1) || (active === -1 && i === this.n - 1 && u > 1);
        el.classList.toggle('is-near', near);
        const img = near && el.querySelector('.nx-walk__shot img[loading="lazy"]');
        if (img) img.loading = 'eager';
        /* only the stop on screen keeps frames; the next one is loaded part-way through (below), everything else is freed */
        const keep = active >= 0 ? i === active || i === active + 1 : (u < 1 ? i === 0 : i === this.n - 1);
        if (active >= 0 && i === active) arm(this.moves[i]);
        else if (active < 0 && keep) arm(this.moves[i]);
        else if (!keep) release(this.moves[i]);
      });
      if (active >= this.n - 1 || (active === -1 && u > 1)) arm(this.film);
      this.ticks.forEach((b, i) => b.setAttribute('aria-current', i === active ? 'true' : 'false'));
    }
    if (active >= 0 && active < this.n - 1 && state[active].t > 0.3) arm(this.moves[active + 1]);
    this.stops.forEach((el, i) => {
      const s = state[i], P = this.parts[i];
      put(P.move, '--m', s.m.toFixed(3));
      put(P.shot, '--a', s.a.toFixed(3));
      put(P.shot, '--h', s.h.toFixed(3));
      put(P.big, '--k', s.k.toFixed(3));
      put(P.card, '--c', s.c.toFixed(3));
      if (el.__on !== s.card) { el.__on = s.card; el.classList.toggle('is-on', s.card); }
    });
  }

  goTo(k) {
    const travel = this.track.offsetHeight - this.stage.offsetHeight;
    const y = this.track.getBoundingClientRect().top - this.top + travel * ((k + 1.7) / this.units);
    const w = document.querySelector('.page-wrapper');
    const opts = { top: y, behavior: 'smooth' };
    if (desk.matches && w && w.scrollHeight > w.clientHeight + 4) w.scrollBy(opts); else window.scrollBy(opts);
  }
}

function init(scope = document) {
  if (!html.classList.contains('nx-motion')) return;
  scope.querySelectorAll('[data-nx-walk]:not([data-walk-on])').forEach((sec) => { sec.nxWalk = new Walk(sec); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
else init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
